import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Text } from 'rebass/styled-components';
import { Button } from '@getro/rombo';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ChevronRight } from 'lucide-react';

import { LinkedinIntegrationCard } from 'pages/Settings/integrations/LinkedinIntegrationCard';
import { GoogleIntegrationCard } from 'pages/Settings/integrations/GoogleIntegrationCard';
import { GoogleIntegrationCardLegacy } from 'pages/Settings/integrations/GoogleIntegrationCardLegacy';
import { GoogleConnectModal } from 'pages/Settings/integrations/GoogleConnectModal';
import { PrivacyNote } from 'pages/Settings/integrations/PrivacyNote';
import { useIntegrationsForm } from './hooks/useIntegrationsForm';

/**
 * Shared integrations form component for onboarding flows.
 * Used by both ally onboarding and network admin onboarding.
 */
const IntegrationsFormContent = ({
  network,
  title,
  description,
  onContinue,
  onSkip,
  isLoading,
  linkedinSyncBasePath,
  linkedinSyncReturnTo,
  containerStyle,
  canSkipWithoutIntegration,
  onGoogleSyncComplete,
}) => {
  const {
    googleSync,
    googleLogin,
    linkedinSync,
    hasGoogleSyncFeature,
    hasRelationshipStrength,
    googleConnectModalOpen,
    initialConnectPreferences,
    openGoogleConnectModal,
    closeGoogleConnectModal,
    submitGooglePreferences,
    isCreatingGoogleAccount,
    isLaunchingGoogleOAuth,
    isUpdatingGoogleAccount,
    allIntegrationsDone,
    anyIntegrationDone,
    getLinkedinSyncLink,
  } = useIntegrationsForm({
    network,
    linkedinSyncBasePath,
    linkedinSyncReturnTo,
    onGoogleSyncComplete,
  });

  const canSkip = canSkipWithoutIntegration || anyIntegrationDone;
  // Onboarding must respect the same flag gate as Personal Settings: when
  // `relationship_strength` is on, connect goes through the toggle/exclusion
  // modal so admins pick their preferences before OAuth, instead of inheriting
  // server defaults. `googleLogin` is wrapped to discard the React
  // SyntheticEvent the card forwards, matching the pre-GET-268 call-site.
  const onGoogleConnect = hasRelationshipStrength ? openGoogleConnectModal : () => googleLogin();
  const GoogleCard = hasRelationshipStrength ? GoogleIntegrationCard : GoogleIntegrationCardLegacy;

  return (
    <Flex
      sx={{
        mx: 'auto',
        pb: '40px',
        px: '16px',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '40px',
        maxWidth: '600px',
        width: '100%',
        ...containerStyle,
      }}
    >
      <Flex sx={{ flexDirection: 'column', gap: '16px', width: '100%' }}>
        <Text as="h1" fontSize="22px" fontWeight="600" lineHeight="1.2" color="text.dark">
          {title}
        </Text>
        <Text color="text.main" fontSize="16px" lineHeight="20px">
          {description}
        </Text>
      </Flex>

      <Flex sx={{ flexDirection: 'column', gap: '16px', width: '100%' }}>
        <LinkedinIntegrationCard
          linkedinSync={linkedinSync}
          getLinkedinSyncLink={getLinkedinSyncLink}
          variant="onboarding"
        />
        {hasGoogleSyncFeature && (
          <GoogleCard googleSync={googleSync} onConnect={onGoogleConnect} variant="onboarding" />
        )}
        <PrivacyNote />
      </Flex>

      {hasRelationshipStrength && (
        <GoogleConnectModal
          isOpen={googleConnectModalOpen}
          isSubmitting={isCreatingGoogleAccount || isLaunchingGoogleOAuth || isUpdatingGoogleAccount}
          initialPreferences={initialConnectPreferences}
          onCancel={closeGoogleConnectModal}
          onSubmit={submitGooglePreferences}
        />
      )}

      <Flex sx={{ width: '100%' }}>
        {allIntegrationsDone ? (
          <Button
            sx={{ width: '100%', justifyContent: 'center' }}
            onClick={onContinue}
            loading={isLoading}
            icon={ChevronRight}
            iconPosition="right"
          >
            Continue
          </Button>
        ) : (
          canSkip && (
            <Button
              size="small"
              variant="secondary"
              sx={{ width: '100%', justifyContent: 'center', border: 'none', p: 0, fontSize: '14px' }}
              onClick={onSkip}
              loading={isLoading}
            >
              Skip
            </Button>
          )
        )}
      </Flex>
    </Flex>
  );
};

IntegrationsFormContent.propTypes = {
  network: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  onContinue: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  linkedinSyncBasePath: PropTypes.string.isRequired,
  linkedinSyncReturnTo: PropTypes.string,
  containerStyle: PropTypes.object,
  canSkipWithoutIntegration: PropTypes.bool,
  onGoogleSyncComplete: PropTypes.func,
};

IntegrationsFormContent.defaultProps = {
  isLoading: false,
  linkedinSyncReturnTo: null,
  containerStyle: {},
  canSkipWithoutIntegration: true,
  onGoogleSyncComplete: undefined,
};

/**
 * Wrapper component that provides GoogleOAuthProvider context.
 */
export const IntegrationsForm = (props) => (
  <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
    <IntegrationsFormContent {...props} />
  </GoogleOAuthProvider>
);

IntegrationsForm.propTypes = IntegrationsFormContent.propTypes;
IntegrationsForm.defaultProps = IntegrationsFormContent.defaultProps;
